export interface ITextOptions {
  paragraphClass?: string;
  subHeaderFirstLevelClass?: string;
  isIgnoreParagraph?: boolean;
}

const postfixAndTrim = (text: string, start: number) => {
  return text.substring(start).trim();
};

const processBlock = (text: string) => {
  let result = '';
  let anchorText = '';
  let anchorHRef = '';
  let resultOffset = 0;
  let state = 'normal';
  const len = text.length;

  for (let i = 0; i < len; i++) {
    const symbol = text[i];

    switch (state) {
      case 'normal':
        if (symbol === '^') {
          state = 'sawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'sawCaret':
        if (symbol === '*') {
          result += '<b>';
          state = 'inCaret';
        } else if (symbol === '&') {
          result += '<i>';
          state = 'inCaret';
        } else if (symbol === '[') {
          anchorText = '';
          anchorHRef = '';
          resultOffset = result.length; // Start location for text.
          state = 'anchorText';
        } else {
          result += symbol;
          state = 'normal';
        }
        break;
      case 'inCaret':
        if (symbol === '^') {
          state = 'inCaretSawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'inCaretSawCaret':
        if (symbol === '*') {
          result += '</b>';
          state = 'normal';
        } else if (symbol === '&') {
          result += '</i>';
          state = 'normal';
        } else {
          result += symbol;
          state = 'inCaret';
        }
        break;
      case 'anchorText':
        if (symbol === '^') {
          state = 'anchorTextSawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'anchorTextSawCaret':
        if (symbol === '|') {
          anchorText = result.substring(resultOffset, result.length);
          result = result.substring(0, resultOffset);
          state = 'anchorBody';
        } else {
          result += '^' + symbol;
          state = 'anchorText';
        }
        break;
      case 'anchorBody':
        if (symbol === '^') {
          state = 'anchorBodySawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'anchorBodySawCaret':
        if (symbol === ']') {
          anchorHRef = result.substring(resultOffset, result.length);
          result = result.substring(0, resultOffset);
          result +=
            '<a class="inline-link" href="' +
            anchorHRef +
            '">' +
            anchorText +
            '</a>';
          state = 'normal';
        } else {
          result += '^' + symbol;
          state = 'anchorBody';
        }
        break;
      default: {
        result += symbol;
      }
    }
  }
  return result;
};

export const getCleanText = (text: string) => {
  return text.trim().replace(/\s{2,}/g, ' ');
};

export const generateMarkdown = (text: string, options?: ITextOptions): string => {
  let result = '';
  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const cleanText = getCleanText(lines[i]);

    if (cleanText !== '') {
      if (cleanText.startsWith('# ')) {
        result +=
          '<h1 class="headline--s">' +
          processBlock(postfixAndTrim(cleanText, 2)) +
          '</h1>\n<hr/>\n';
      } else if (cleanText.startsWith('## ')) {
        const className = options?.subHeaderFirstLevelClass
          ? options.subHeaderFirstLevelClass
          : 'headline--m';

        result +=
          `<h2 class="${className}">` +
          processBlock(postfixAndTrim(cleanText, 3)) +
          '</h2>\n';
      } else if (cleanText.startsWith('### ')) {
        result +=
          '<h1 class="headline--s">' +
          processBlock(postfixAndTrim(cleanText, 4)) +
          '</h1>\n<hr/>\n';
      } else if (cleanText.startsWith('#### ')) {
        result +=
          '<h1 class="headline--m">' +
          processBlock(postfixAndTrim(cleanText, 5)) +
          '</h1>\n';
      } else if (cleanText.startsWith('@credit ')) {
        result +=
          '<p class="credit">' +
          processBlock(postfixAndTrim(cleanText, 8)) +
          '</p>\n';
      } else if (cleanText.startsWith('@hr')) {
        result += '<hr/>\n';
      } else if (cleanText.startsWith('@image(')) {
        const [tag, src, width, height] =
        cleanText.match(/^@image\(([^,]+),([^,]+),([^)]+)\)$/) || [];

        if (tag) {
          result += `<img src="${src}" width="${width}" height="${height}" class="image" />`;
        }
      } else if (cleanText.startsWith('@link(')) {
        const found2 = cleanText.match(/^@link\(([^)]+)\)\s+(.+)$/);
        if (found2 !== null) {
          result +=
            '<a href="' +
            found2[1] +
            '" class="source_link">' +
            found2[2] +
            '</a>\n';
        }
      } else if (cleanText.startsWith('@cta(')) {
        const [tag, href, text] =
        cleanText.match(/^@cta\(([^)]+)\)\s+(.+)$/) || [];

        if (tag) {
          result += '<a href="' + href + '" class="button">' + text + '</a>\n';
        }
      } else if (cleanText.startsWith('@sp')) {
        result += '<p>&nbsp;</p>\n';
      } else if (cleanText.startsWith('@ad')) {
        const mackResult = cleanText.match(/^@ad\(([^)]+)\)/) || [];
        const slotID = mackResult[1];

        if (slotID) {
          result += `<div class="ad-wrapper"><div id="ad_unit_${slotID}"></div></div>`;
        }
      } else if (!options?.isIgnoreParagraph) {
        result +=
          `<p class="${
            options?.paragraphClass ? options.paragraphClass : 'paragraph--s'
          }">` +
          processBlock(postfixAndTrim(cleanText, 0)) +
          '</p>\n';
      } else {
        result += processBlock(postfixAndTrim(cleanText, 0));
      }
    }
  }
  return result;
};

export const stripHTML = str => str?.replace(/(<([^>]+)>)/ig, '');

export const removeAllMarkdown = (text: string, options?: ITextOptions): string => {
  return stripHTML(generateMarkdown(text, options));
};